/**
 * Logo easter egg runtime. `LogoEggProvider` sits at the root of a screen and
 * owns a touch-transparent overlay; `LogoEggTrigger` makes the logo inside it
 * tappable. A tap plays the next step of the curated loop
 * (lib/logoEasterEgg), the logo's carp is hidden and an identical carp on the
 * overlay performs the scene, then hands back. Without a provider the trigger
 * renders the logo untouched.
 *
 * Each signed-in user's place in the loop is kept locally on this device
 * (lib/storage: localStorage on web, SecureStore on native; nothing goes to
 * the server), so coming back carries on at the next unseen reaction. After
 * the last step the saved progress is cleared and the loop restarts at step 1.
 * Loading is asynchronous and never blocks a tap: taps before it lands play
 * from step 1 in memory, and resumeLogoProgress skips past them and the saved
 * steps once it arrives. Without a user id progress lives in memory only.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Animated, Easing, Pressable, StyleSheet, View } from 'react-native'
import { useReducedMotion } from 'react-native-reanimated'
import { BRAND_LOGO_TEXT_CENTER } from '@warcarp/ui'
import {
  chooseLogoReaction, logoProgressStorageKey, logoShotSeed, parseLogoProgress, resumeLogoProgress, serializeLogoProgress,
  type LogoEnemy, type LogoReaction,
} from '../../../lib/logoEasterEgg'
import { FISH_ASPECT } from '../../../lib/logoEggArt'
import { buildLogoScene, type LogoScene } from '../../../lib/logoEggScene'
import { reportError } from '../../../lib/reporting'
import { getItem, removeItem, setItem } from '../../../lib/storage'
import { LogoEggStage } from './LogoEggStage'

type Measure = (callback: (x: number, y: number, width: number, height: number) => void) => void
interface LogoHandle { measure: Measure; height: number; centerOnWordmark: boolean; variant: 'light' | 'dark' }
export interface LogoEggApi {
  register: (logo: LogoHandle | null) => void
  /** A tap: picks the reaction. Ignored while one is running. */
  tap: () => void
  /** Plays a specific reaction (preview page). Ignored while one is running. */
  play: (reaction: LogoReaction, options?: { enemies?: LogoEnemy[]; reducedMotion?: boolean }) => void
  /** True while the overlay carp stands in for the logo's. */
  fishHidden: boolean
}

const LogoEggContext = createContext<LogoEggApi | null>(null)
export const useLogoEgg = () => useContext(LogoEggContext)

function saveProgress(userId: string, next: number) {
  // Best effort: a failed write only means a repeat after the next launch.
  // Step 0 is a finished loop: clear the history so the next visit starts over.
  const key = logoProgressStorageKey(userId)
  ;(next === 0 ? removeItem(key) : setItem(key, serializeLogoProgress(next))).catch(() => {})
}

export function LogoEggProvider({ children, userId, random = Math.random }: { children: React.ReactNode; userId?: string | null; random?: () => number }) {
  const reducedMotion = useReducedMotion()
  const overlay = useRef<View>(null)
  const logo = useRef<LogoHandle | null>(null)
  /** The step of the loop the next tap plays. */
  const position = useRef(0)
  // Per user: has the saved progress been read yet, and how many taps played before it was.
  const store = useRef<{ userId: string | null; loaded: boolean; playedBeforeLoad: number }>({ userId: null, loaded: true, playedBeforeLoad: 0 })
  const randomRef = useRef(random); randomRef.current = random
  const running = useRef(false)
  /** Plays so far on this screen: with the loop step, it seeds where the targets line up. */
  const plays = useRef(0)
  const progress = useRef(new Animated.Value(0)).current
  const [active, setActive] = useState<{ scene: LogoScene; variant: 'light' | 'dark' } | null>(null)

  const start = useCallback((reaction: LogoReaction, options: { enemies?: readonly LogoEnemy[]; reducedMotion?: boolean }, step = -1) => {
    const handle = logo.current, stage = overlay.current
    if (running.current || !handle || !stage) return
    running.current = true
    const seed = logoShotSeed(step, plays.current++)
    const fail = (error: unknown) => { running.current = false; setActive(null); reportError(error, { tags: { area: 'logo-easter-egg' } }) }
    try {
      stage.measureInWindow((ox, oy, width, height) => handle.measure((lx, ly) => {
        try {
          if (!(width > 0 && height > 0)) { running.current = false; return }
          // The header logo is nudged so its wordmark centres on the row (BrandMark centerOnWordmark).
          const nudge = handle.centerOnWordmark ? (0.5 - BRAND_LOGO_TEXT_CENTER) * handle.height : 0
          const fish = { x: lx - ox, y: ly - oy + nudge, width: handle.height * FISH_ASPECT, height: handle.height }
          setActive({ scene: buildLogoScene(reaction, { stage: { width, height }, fish }, { ...options, seed }), variant: handle.variant })
        } catch (error) { fail(error) }
      }))
    } catch (error) { fail(error) }
  }, [])

  // Start once the stage has mounted, so the native driver owns every view it animates.
  useEffect(() => {
    if (!active) return
    progress.setValue(0)
    const animation = Animated.timing(progress, { toValue: 1, duration: active.scene.duration, easing: Easing.linear, useNativeDriver: true })
    animation.start(() => { running.current = false; setActive(null) })
    return () => animation.stop()
  }, [active, progress])

  useEffect(() => {
    const id = userId ?? null
    const state = { userId: id, loaded: !id, playedBeforeLoad: 0 }
    store.current = state
    position.current = 0
    if (!id) return
    getItem(logoProgressStorageKey(id)).catch(() => null).then(raw => {
      if (store.current !== state) return
      const stored = parseLogoProgress(raw)
      // Rewrite whenever the saved form differs (old format, other sequence, early taps).
      if (stored !== null || state.playedBeforeLoad) {
        position.current = resumeLogoProgress(stored ?? 0, state.playedBeforeLoad)
        if (raw !== serializeLogoProgress(position.current)) saveProgress(id, position.current)
      }
      state.loaded = true
    }, () => { state.loaded = true })
  }, [userId])

  const register = useCallback((handle: LogoHandle | null) => { logo.current = handle }, [])
  const reducedRef = useRef(reducedMotion); reducedRef.current = reducedMotion
  const tap = useCallback(() => {
    if (running.current || !logo.current) return
    const step = position.current
    const choice = chooseLogoReaction(step, { reducedMotion: !!reducedRef.current, random: randomRef.current })
    if (!choice.reducedMotion) {
      position.current = choice.next
      const state = store.current
      // Until the saved progress is read, count what played instead of overwriting it.
      if (!state.loaded) state.playedBeforeLoad++
      else if (state.userId) saveProgress(state.userId, choice.next)
    }
    start(choice.reaction, choice, step)
  }, [start])
  const play = useCallback((reaction: LogoReaction, options: { enemies?: LogoEnemy[]; reducedMotion?: boolean } = {}) => start(reaction, options), [start])
  const api = useMemo(() => ({ register, tap, play, fishHidden: active !== null }), [register, tap, play, active])

  return <LogoEggContext.Provider value={api}>
    {children}
    <View ref={overlay} collapsable={false} pointerEvents="none" style={[StyleSheet.absoluteFill, { zIndex: 50, elevation: 50 }]} testID="logo-egg-overlay">
      {active && <LogoEggStage scene={active.scene} progress={progress} variant={active.variant} />}
    </View>
  </LogoEggContext.Provider>
}

/**
 * Wraps the logo so a tap starts a reaction. The press target adds no layout
 * and no extra screen-reader element: the parent keeps its header role and
 * "WarCarp. Signed in as …" label.
 */
export function LogoEggTrigger({ height, variant, centerOnWordmark = false, children }: {
  height: number; variant: 'light' | 'dark'; centerOnWordmark?: boolean; children: (fishHidden: boolean) => React.ReactNode
}) {
  const egg = useLogoEgg()
  const ref = useRef<View>(null)
  const register = egg?.register
  useEffect(() => {
    if (!register) return
    register({ measure: callback => ref.current?.measureInWindow(callback), height, centerOnWordmark, variant })
    return () => register(null)
  }, [register, height, centerOnWordmark, variant])
  if (!egg) return <>{children(false)}</>
  return <Pressable ref={ref} collapsable={false} onPress={egg.tap} accessible={false} focusable={false} testID="home-brand-logo-egg">
    {children(egg.fishHidden)}
  </Pressable>
}
